import * as stagesCase from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import {
  dateFieldDefault,
  emailDefault,
  faxNumberDefault,
  ibanFieldDefault,
  postalCodeDefault,
  textFieldDefault,
} from "../../../validationRules";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import addressFields from "../modules/addressFields";
import InputAdornment from "@mui/material/InputAdornment";
import React from "react";
import _ from "lodash";
import { UseForm } from "../../../../types/UseForm";
import DateChangeButtonsToolbar from "../../../../components/Validator/DateChangeButtonsToolbar";
import legalForms from "../../../legalForms";
import {
  getDriverOfClientVehicleOptions,
  getTypeOfAccidentOptions,
  getTypeOfDamageOptions,
} from "./trafficAccidentFormHelperService";
import { IbanInput } from "../../../../components/MaskedInputs/IbanInput";
import { TrafficAccident } from "../../../../types/Products/TrafficLaw/TrafficAccident/TrafficAccident";
import getExtrajudicialRecommendationOptions from "../abstractLitigationCase/extrajudicialRecommendationOptions";
import {
  addProcessParticipant,
  caseInsurance,
  litigationCourt,
  extraJudicialProcedure,
  getNextInstanceSection,
  judicialProcedure,
  opponentLawyer,
  processParticipantsAuthority,
  processParticipantsOrganizations,
  processParticipantsPeople,
} from "../abstractLitigationCase/fullFormDefinition";
import { translate } from "../../../Translations/translatorService";

import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";
import { BackofficeUser } from "../../../../types/BackofficeUser";

export const fullFormDefinition = (product: TrafficAccident, currentUser: BackofficeUser) => {
  const trafficAccidentBaseCreated = {
    isHidden: ({ product }: { product: TrafficAccident }) => product.stage > stagesCase.STAGE_CREATED,
    label: "Unfall",
    elements: [],
  };

  const trafficAccidentBase = {
    isHidden: ({ product }: { product: TrafficAccident }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
    label: "Unfall",
    elements: [
      ...personFields({ path: `${product.productClassName}.client` }),
      ...addressFields({
        path: `${product.productClassName}.client.residenceAddress`,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.client.bankData.iban`,
        validators: ibanFieldDefault,
        additionalProps: {
          InputProps: {
            inputComponent: IbanInput,
            startAdornment: <InputAdornment position="start">DE</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.typeOfAccident`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage! >= stagesCase.STAGE_CHECKED,
        options: getTypeOfAccidentOptions,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.typeOfDamage`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        options: getTypeOfDamageOptions,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.accidentDate`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.accidentTime`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        additionalProps: {
          type: "time",
          InputLabelProps: {
            shrink: true,
          },
        },
      },
      ...addressFields({
        path: `${product.productClassName}.accidentAddress`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
      }),
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.policeInvolved`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.policeInvolved`),
      },
    ],
  };

  const courseOfEvents = {
    isHidden: ({ product }: { product: TrafficAccident }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
    label: "Unfallhergang",
    elements: [
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.courseOfEvents`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          rows: 12,
          fullWidth: true,
        },
      },
    ],
  };

  const damageDetails = {
    isHidden: ({ product }: { product: TrafficAccident }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
    label: "Details zum Schaden",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.driverOfClientVehicle`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        options: getDriverOfClientVehicleOptions,
      },
      ...personFields({
        path: `${product.productClassName}.driverPerson`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          !(values.trafficAccident.driverOfClientVehicle === "other"),
      }),
      ...addressFields({
        path: `${product.productClassName}.driverPerson.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          !(values.trafficAccident.driverOfClientVehicle === "other"),
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.licensePlateClientVehicle`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.vehicleMake`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.vehicleModel`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.vehicleId`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.legalExpenseInsurance`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.legalExpenseInsurance`),
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.descriptionInjuries`,
        isDisabled: () => true,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          !["vehicleAndPerson", "person"].includes(values.trafficAccident.typeOfDamage),
        additionalProps: {
          multiline: true,
          rows: 8,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.descriptionDamage`,
        isDisabled: () => true,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          !["vehicleAndPerson", "vehicle"].includes(values.trafficAccident.typeOfDamage),
        additionalProps: {
          multiline: true,
          rows: 8,
          fullWidth: true,
        },
      },
    ],
  };

  const opposingParty = {
    isHidden: ({ product }: { product: TrafficAccident }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
    label: "Gegenseite",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyType`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        options: () => [{ value: "opposingPartyLegalEntity" }, { value: "opposingPartyPerson" }],
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.legalName`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: TrafficAccident }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.legalForm`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: TrafficAccident }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        options: () => [
          { value: "__null__", label: "Keine Gesellschaftsform" },
          ..._.map(legalForms, (legalForm) => {
            return {
              value: legalForm.label,
              label: legalForm.label,
            };
          }),
        ],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.streetAddress`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postalCode`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.addressLocality`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxNumber`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxPostalCode`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxAddressLocality`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.mainEmail.email`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.telephone`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.fax`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
        validators: faxNumberDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">+49</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyReference`,
        helperText: translate(`${product.productClassName}.opposingPartyReference.helperText`),
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyLegalEntity",
      },
      {
        type: "ContractRepresentatives",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.additionalRepresentatives`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.opposingPartyType === "opposingPartyLegalEntity",
        additionalProps: {
          fullWidth: true,
          gridSpacing: 4,
          gridSize: 4,
          buttonFullWidth: false,
        },
      },
      ...personFields({
        path: `${product.productClassName}.opposingPartyPerson`,
        allowNoGender: true,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: TrafficAccident; values: UseForm["values"] }) =>
          product.stage >= stagesCase.STAGE_COMPLETED,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyPerson.email`,
        validators: emailDefault,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyPerson",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyPerson.telephone`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyPerson",
      },
      ...addressFields({
        path: `${product.productClassName}.opposingPartyPerson.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.trafficAccident.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.licensePlateOpposingParty`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: () => false,
        validators: textFieldDefault,
      },
    ],
  };

  const opposingPartyLiabilityInsurance = {
    label: "KFZ-Haftpflicht Gegenseite",
    elements: [
      {
        type: "ValidatorOrganizationAutocomplete",
        path: `${product.productClassName}.opposingCarLiabilityInsurance.organization`,
        additionalProps: {
          organizationType: "opposingCarLiabilityInsurance",
        },
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingInsurancePolicyNumber`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_PREPARE_LAWSUIT,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingCaseReferenceNumber`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_PREPARE_LAWSUIT,
      },
    ],
  };

  let extraJudicialProcedureDefinition = extraJudicialProcedure(
    product,
    currentUser,
    [
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.additionalIssueDescriptionText`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: TrafficAccident }) =>
          product.isLawsuitHandling ||
          (product.stage >= stagesCase.STAGE_CHECKED && !product.additionalIssueDescriptionText),
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.additionalClaimText`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: TrafficAccident }) =>
          product.isLawsuitHandling || product.stage || (0 >= stagesCase.STAGE_CHECKED && !product.additionalClaimText),
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.defectsDescriptionLawFirm`,
        isDisabled: ({ product }: { product: TrafficAccident }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: TrafficAccident }) =>
          !_.has(product, "defectsDescriptionLawFirm") || product.caseCategory !== "enforceClaim",
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.courseOfEventsLawFirm`,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          !product.stage ||
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      },
      {
        type: "SelectAndExtendedDataComboField",
        path: `${product.productClassName}.claimsForDamages`,
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          !product.stage ||
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
        additionalProps: {
          fullWidth: true,
          config: {
            values: [
              "repairCosts",
              "replacementCosts",
              "lossOfUse",
              "rentalCarCosts",
              "depreciation",
              "expertFees",
              "expensesFlatFee",
              "deregistrationCosts",
              "registrationCosts",
              "towingCosts",
              "storageCosts",
              "financingCosts",
            ],
            showOther: true,
            extensionFieldConfig: {
              type: "money",
              labelPrefix: "Höhe Schadensersatz",
              md: 4,
            },
          },
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.typeOfDamage`,
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          !product.stage ||
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
        options: () => [{ value: "vehicle" }, { value: "person" }, { value: "vehicleAndPerson" }, { value: "other" }],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.typeOfAccident`,
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          !product.stage ||
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
        options: () => [
          { value: "rearEndCollision" },
          { value: "laneChangeCollision" },
          { value: "parking" },
          { value: "other" },
        ],
      },
      {
        type: "ExpandableTextField",
        path: `${product.productClassName}.courseOfEvents`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.finalProcessFeedbackClient`, // Rückmeldung Mandant
        translationPath: `${product.productClassName}.finalProcessFeedbackClient.${product.caseCategory}`, // Rückmeldung Mandant
        helperText:
          "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
          "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
        isHidden: ({ product }: { product: TrafficAccident }) => product.finalClaimStatusClient === "fullyAgreed",
        options: () => [{ value: "agreement" }, { value: "lawsuit" }],
      },
    ],
    () => [{ value: "none" }, { value: "individual" }],
    getExtrajudicialRecommendationOptions,
  );

  /* Filter out different and not necessary fields */
  let excludeElementsWithFullPath = [
    "extrajudicialCommunicationDeadline",
    "hasMonitoringBilling",
    "finalClaimStatusClient",
    "finalProcessFeedbackClient",
  ].map((path) => `${product.productClassName}.${path}`);

  extraJudicialProcedureDefinition.elements = extraJudicialProcedureDefinition.elements.filter(
    (config) => !excludeElementsWithFullPath.includes(config.path),
  );

  /* Redefined fields which are different */
  extraJudicialProcedureDefinition.elements = extraJudicialProcedureDefinition.elements.concat([
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
      validators: dateFieldDefault,
      isDisabled: ({ product }: { product: TrafficAccident }) =>
        !product.stage ||
        product.stage < stagesCase.STAGE_DATA_COMPLETED ||
        product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      additionalProps: {
        toolbar: DateChangeButtonsToolbar,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.finalProcessFeedbackClient`, // Rückmeldung Mandant
      translationPath: `${product.productClassName}.finalProcessFeedbackClient.${product.caseCategory}`, // Rückmeldung Mandant
      helperText:
        "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
        "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
      isDisabled: ({ product }: { product: TrafficAccident }) =>
        product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
      isHidden: ({ product }: { product: TrafficAccident }) => product.finalClaimStatusClient === "fullyAgreed",
      options: () => [{ value: "agreement" }, { value: "lawsuit" }],
    },
  ]);

  return {
    product: {
      sections: [trafficAccidentBaseCreated, trafficAccidentBase, courseOfEvents, damageDetails],
    },
    additional: {
      sections: [
        {
          label: "Allgemeines",
          elements: [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.reference`,
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.powerOfAttorney`,
              isHidden: ({ product }: { product: TrafficAccident }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
              isDisabled: () => true,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.legalExpenseInsurance`,
              isDisabled: ({ product }: { product: TrafficAccident }) =>
                product.stage > stagesCase.STAGE_ACTION_REQUIRED,
              options: () => trueFalseDefaultOptions("trafficAccident.legalExpenseInsurance"),
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.caseCategoryAssessment`,
              isHidden: ({ product }: { product: TrafficAccident }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
              isDisabled: ({ product }: { product: TrafficAccident }) =>
                product.stage > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
              options: () => [{ value: "confirm" }, { value: "reject" }],
            },
          ],
        },
        extraJudicialProcedureDefinition,
        judicialProcedure(product),
        {
          label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
          isHidden: ({ product }: { product: TrafficAccident }) => product.courtAppointments.length === 0,
          elements: getCourtAppointmentFields(product),
        },
        getNextInstanceSection(product),
      ],
    },
    processParticipants: {
      asAccordion: true,
      sections: [
        opposingParty,
        processParticipantsAuthority(product),
        caseInsurance(product),
        opposingPartyLiabilityInsurance,
        opponentLawyer(product),
      ],
    },
    additionalProcessParticipants: {
      isHidden: () => product.advisoryHandling,
      sections: [
        addProcessParticipant(),
        processParticipantsPeople(product),
        processParticipantsOrganizations(product),
      ],
      withoutSubmit: true,
    },
    court: {
      isHidden: () => product.advisoryHandling,
      sections: [
        litigationCourt(product, ["Amtsgericht", "Arbeitsgericht", "Landgericht", "Mahngericht", "Oberlandesgericht"]),
      ],
    },
  };
};
